import React, { useState } from "react";
import HourAndDelete from "./HourAndDelete";
import { TextButton } from "../../../controls";
import ClassNames from "classnames";
import Tooltip from "../../Tooltip/Tooltip";
import type { TextInputSize } from "../../../controls/TextInput/TextInput";

// Props SchedulerTimes injects into HourSelectionComponent via HourAndDelete
// (see HourSelection.js — still JS, being converted in parallel).
interface HourSelectionComponentProps {
  hour: string;
  onChange: (hour: string) => void;
  timeIntervals?: number;
  includeTimes?: Date[];
  isClearable?: boolean;
}

interface SchedulerTimesProps {
  className?: string;
  "data-testid"?: string;
  HourSelectionComponent: React.ComponentType<HourSelectionComponentProps>;
  initialTimes?: string[];
  onChange: (times: string[]) => void;
  maxTimeSlots?: number;
  minTimeSlots?: number;
  label?: string;
  hideLabelHelper?: boolean;
  labelTop?: boolean;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: TextInputSize;
  labelWidth?: string | boolean;
  isInline?: boolean;
  isLarge?: boolean;
  noMargin?: boolean;
  error?: boolean | string;
  withHelp?: string;
  suffixIconButton?: React.ReactNode;
}

const generateValidTimes = (selectedTime: string): Date[] => {
  const [hours, minutes] = selectedTime.split(":");
  const times: Date[] = [];
  for (let i = 0; i < 24; i++) {
    const time = new Date();
    if (i.toString().padStart(2, "0") !== hours) {
      time.setHours(i, Number(minutes), 0, 0);
      times.push(time);
    }
  }
  return times;
};

const SchedulerTimes: React.FC<SchedulerTimesProps> = ({
  className,
  "data-testid": dataTestId,

  HourSelectionComponent,
  initialTimes = ["00:00"],
  onChange,
  maxTimeSlots = 2,
  minTimeSlots = 1,
  label = "",
  hideLabelHelper = false,
  labelTop = false,
  required = true,
  disabled = false,
  readOnly = false,
  leftIcon = null,
  rightIcon = null,
  size = "max",
  labelWidth = false,
  isInline = false,
  isLarge = false,
  noMargin = false,
  error,
  withHelp = "",
  suffixIconButton = null,
}) => {
  const [times, setTimes] = useState(initialTimes);

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-weekday-selection",
    // wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "has-left-icon": leftIcon,
      "has-right-icon": rightIcon,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "is-inline": isInline,
      "is-large": isLarge,
      "has-no-margin": noMargin,
      "has-error": error,
      "with-help": withHelp?.length,
      "has-suffix-icon-btn": suffixIconButton,
    },
    className
  );
  const onTimeDelete = (index: number) => {
    setTimes((current) => {
      const newTimes = current.filter((_, i) => i !== index);
      onChange(newTimes);
      return newTimes;
    });
  };

  const handleHourChange = (index: number, hour: string) => {
    const newData = [...times];
    newData[index] = hour;
    if (index === 0) {
      newData.forEach((time, i) => {
        if (i > 0) {
          newData[i] = time.split(":")[0] + ":" + hour.split(":")[1];
        }
      });
    }

    setTimes(newData);
    onChange(newData);
  };

  return (
    <div className={"scheduler-times-wrap"}>
      <div
        className={`scheduler-times-content ${classNameWrapper} no-padding-bottom`}
      >
        {!!label && (
          <label
            className={`input-label`}
            style={{ width: labelWidth ? String(labelWidth) : "" }}
            data-testid={`${dataTestId}-label`}
          >
            {label}
            {!required && !hideLabelHelper && (
              <span className={"label-helper"}>Optional</span>
            )}
            {withHelp && (
              <>
                <Tooltip type="info" content={withHelp}>
                  <i className={`seem-icon seem-icon-help`} />
                </Tooltip>
              </>
            )}
          </label>
        )}
        {times.map((time, index) => {
          return (
            <HourAndDelete
              HourSelectionComponent={HourSelectionComponent}
              time={time}
              key={index}
              showDelete={times.length > minTimeSlots}
              onDelete={() => onTimeDelete(index)}
              onChange={(hour) => handleHourChange(index, hour)}
              includeTimes={
                index > 0 ? generateValidTimes(times[0]) : undefined
              }
              timeIntervals={15}
              isClearable={false}
            />
          );
        })}
      </div>
      {times?.length < maxTimeSlots && (
        <TextButton
          buttonStyle="textLink"
          label={"+ Add Time"}
          data-testid="daily-runner-add-time"
          onClick={(e) => {
            const minutes = times[0].split(":")[1];
            const newTime = `00:${minutes}`;
            const newTimes = [...times, newTime];
            setTimes(newTimes);
            onChange(newTimes);
            e?.stopPropagation();
          }}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default SchedulerTimes;
